import { api } from '../../core/network/api';
import type { LootDrop } from './map-api';

export type MobAction = 'strike' | 'heavy' | 'guard' | 'ability' | 'flee';
export type CombatEvent = 'hit' | 'crit' | 'miss' | 'block' | 'ability' | null;

export interface RoundOutcome {
  round: number; action: MobAction; abilityName: string | null;
  heroDmg: number; heroEvent: CombatEvent; heroHeal: number;
  mobDmg: number; mobEvent: CombatEvent; bleedDmg: number;
  heroHp: number; mobHp: number; cooldown: number;
  fled: boolean; fleeFailed: boolean; ended: boolean; victory: boolean;
}

export interface FightView {
  active: boolean;
  round: number;
  cooldown: number;
  ability: { name: string; desc: string; ready: boolean };
  hero: { hp: number; maxHp: number; class: string };
  enemy: { name: string; type: string; level: number; archetype?: string; hp: number; maxHp: number };
  healsLeft: number;
  /** Mid-fight patch-up: its RUNE cost (QA 15.08 — the heal is the premium
   *  convenience now), and whether the player can currently afford it
   *  (balance ≥ cost, a heal remaining, and not already at full HP).
   *  `healAffordable` lets the client grey the card out instead of letting the
   *  player click into an "Insufficient balance" error. */
  healCost: number;
  /** Once-per-fight rune refill — the Horn of Mead (QA 15.08). Refills the
   *  fight's VIGOR pool since item 9 (31.08). */
  furyRestore?: { cost: number; used: boolean; affordable: boolean } | null;
  /** The fight's own energy (item 9, 31.08) — spent by the big moves, +1 a
   *  round, never the hero's persistent fury. */
  vigor?: { current: number; max: number } | null;
  /** Vigor price of the big moves this fight (heavy/guard/ability/flee). */
  actionVigor?: { heavy: number; guard: number; ability: number; flee?: number } | null;
  /** Per-fight numbers for each move (QA 15.08) — real ranges computed from
   *  this fight's own attack/defense, shown on the card tooltips. */
  moves?: {
    strike: { min: number; max: number; critPct: number };
    heavy: { min: number; max: number; hitPct: number; extraTakenPct: number };
    guard: { dealPct: number; takenPct: number; blockPct: number };
    flee: { successPct: number };
    healPct: number;
  } | null;
  healAffordable: boolean;
  /** Seconds until the idle session goes cold (server TTL, refreshed on every
   *  action). Drives the on-screen countdown + graceful cold-fight ending. */
  expiresInSec: number;
  kind: 'mob' | 'garrison' | 'heist' | 'arena' | 'defend';
  /** Garrison squad progress (ground takeover) or raid room progress: which
   *  defender/room is up, of how many. */
  wave: { index: number; total: number } | null;
  outcome: RoundOutcome | null;
  rewards: { cash: number; xp: number; level: number; leveledUp: boolean } | null;
  loot: LootDrop | null;
  captured: boolean | null;
  /** Defend only: did this bout actually break the siege? */
  defended?: boolean | null;
  result: 'VICTORY' | 'DEFEAT' | null;
  fled: boolean;
  /** Raid-only progress: banked take, whether we're between rooms, etc. */
  heist?: {
    dungeonId: string;
    bankedCash: number;
    awaitingNext: boolean;
    nextEnemyName: string | null;
    timeExpired?: boolean;
    revived?: boolean;
    /** This room was already banked by an earlier attempt — won, but no cut. */
    reclaimed?: boolean;
    /** What THIS room just paid (post-buff), for the room-clear plate — the
     *  silver icon after each raid level. `bankedCash` is the running total. */
    roomCash?: number;
  };
  /** Arena-only: present on the FINISHED view (active:false) of a Pit bout —
   *  the rating/gold settle-up. Null on a fled bout (no-contest). */
  arena?: {
    won: boolean;
    ratingChange: number;
    rating: number;
    title: string;
    wins: number;
    losses: number;
    streak: number;
    goldWon: number;
    goldLost: number;
    /** Saga standing the bout actually moved, signed — the flat stake the
     *  pre-fight terms quoted, confirmed on the outcome (QA 86cb8zdku). */
    rankPointsChange: number;
  } | null;
}

export const startFight = (col: number, row: number) =>
  api.post<FightView>('/mobfight/start', { col, row });
/** Begin an interactive ground takeover vs a district garrison. */
export const startGarrison = (districtId: string) =>
  api.post<FightView>('/mobfight/garrison', { districtId });
/** Begin an interactive ward DEFENSE — break the siege on your own ground. */
export const startDefend = (districtId: string) =>
  api.post<FightView>('/mobfight/defend', { districtId });
/** Begin (or resume) an interactive raid — a multi-room job vs a dungeon. */
export const startHeist = (dungeonId: string) =>
  api.post<FightView>('/mobfight/heist', { dungeonId });
/** Begin (or resume) an interactive arena bout (the Holmgang) vs a synthesized bot. */
export const startArena = () => api.post<FightView>('/mobfight/arena', {});
export const fightRound = (action: MobAction) =>
  api.post<FightView>('/mobfight/round', { action });
export const fightHeal = () => api.post<FightView>('/mobfight/heal', {});
/** Once-per-fight rune fury refill — the Horn of Mead, pulled mid-fight. */
export const fightRestoreFury = () => api.post<FightView>('/mobfight/fury-restore', {});
/** The live bout, or null. Read-only — never starts one, never spends fury. */
export const fetchCurrentFight = () => api.get<FightView | null>('/mobfight/current');
/** Between raid rooms: press on to the next room (HP + cooldown carry). */
export const continueHeist = () => api.post<FightView>('/mobfight/continue', {});
/**
 * Bail out of the live bout. Always `ok: true`; for a bout other than ARENA
 * that's the whole response (HP just persists, nothing else to report). For
 * ARENA (the Holmgang), walking out mid-fight is a FORFEIT — it settles exactly
 * like losing the last round (rating drop + stake burn) — so the FightView
 * fields come back too, spread onto the same object, letting the caller show
 * the settled LOSS outcome instead of just closing the screen. Check for
 * `result` before trusting the FightView fields (they're absent otherwise).
 */
export const fightAbandon = () => api.post<{ ok: true } & Partial<FightView>>('/mobfight/abandon', {});
